import type { PropsWithChildren } from "react";

import type { BreadcrumbsList } from "@ui/eva-components/Breadcrumbs";
import classNames from "classnames";

import { Container } from "@jti/core-components/components/Container";

import type { LogoProps } from "../Logo";
import { type RenderLinkProps } from "../types/RenderLinkProps.type";
import { renderPlainLink } from "../utils/renderPlainLink";

import type { CopyrightProps } from "./components/Copyright";
import { Copyright } from "./components/Copyright";
import { FooterBreadcrumbs } from "./components/FooterBreadcrumbs";
import { FooterIcons } from "./components/FooterIcons/FooterIcons";
import type { FooterNavProps } from "./components/FooterNav";
import { FooterNav } from "./components/FooterNav";
import { FooterRenderZone } from "./components/FooterRenderZone/FooterRenderZone";
import type { HealthBannerProps } from "./components/HealthBanner";
import { FooterHealthBanner } from "./components/HealthBanner/FooterHealthBanner";

import styles from "./Footer.module.scss";

export type FooterProps = {
  logoProps: LogoProps;
  showUpdatedFooterUI?: boolean;
  footerNavProps: FooterNavProps;
  copyrightProps: CopyrightProps;
  healthBannerProps?: HealthBannerProps;
  isExtended: boolean;
  enableFooterBottomZone?: boolean;
  hideFirstBottomZone?: boolean;
  firstBottomZoneTitle?: string;
  renderFirstBottomZone?: () => React.ReactNode;
  hideSecondBottomZone?: boolean;
  secondBottomZoneTitle?: string;
  renderSecondBottomZone?: () => React.ReactNode;
  renderTopBanner?: () => React.ReactNode;
  enableFooterTopZone?: boolean;
  renderFooterTopZone?: () => React.ReactNode;
  breadcrumbsList: BreadcrumbsList;
  socialTitle?: string;
  socialIcons?: {
    alt: string;
    icon: string;
    link: string;
  }[];
  paymentTitle?: string;
  paymentIcons?: {
    alt: string;
    icon: string;
    href?: string;
    target?: "_blank" | "_self";
    width?: number;
    height?: number;
  }[];
  renderSocialIcon?: (props: { src: string; alt: string }) => JSX.Element;
  flipIconsOrder?: boolean;
  enableTopBanner?: boolean;
  withListComponent?: boolean;
  theme?: "dark" | "light";
  ariaLabels?: { footerCookiesSettingsLink: string };
  openAssetsInNewTab?: boolean;
  withdrawalModalTriggerUrl?: string;
  onOpenWithdrawalModal?: () => void;
} & RenderLinkProps;

export const Footer = ({
  logoProps,
  showUpdatedFooterUI,
  footerNavProps,
  copyrightProps,
  healthBannerProps,
  isExtended,
  enableFooterBottomZone = false,
  hideFirstBottomZone,
  firstBottomZoneTitle,
  renderFirstBottomZone,
  hideSecondBottomZone,
  secondBottomZoneTitle,
  renderSecondBottomZone,
  children,
  breadcrumbsList,
  renderLink = renderPlainLink,
  enableFooterTopZone = false,
  renderFooterTopZone,
  socialTitle,
  socialIcons,
  paymentTitle,
  paymentIcons,
  renderSocialIcon,
  flipIconsOrder,
  enableTopBanner = false,
  renderTopBanner,
  withListComponent = false,
  theme,
  ariaLabels,
  openAssetsInNewTab,
  withdrawalModalTriggerUrl,
  onOpenWithdrawalModal,
}: PropsWithChildren<FooterProps>) => {
  const BottomZone = (
    <>
      <FooterRenderZone
        dataTestid="footerFirstBottomZone"
        renderZone={renderFirstBottomZone}
        zoneTitle={firstBottomZoneTitle}
        containerClass={styles.footerBottomZoneUpdated}
        hideZone={hideFirstBottomZone}
        showUpdatedFooterUI={showUpdatedFooterUI}
      />
      <FooterRenderZone
        dataTestid="footerSecondBottomZone"
        renderZone={renderSecondBottomZone}
        zoneTitle={secondBottomZoneTitle}
        containerClass={styles.footerBottomZoneUpdated}
        hideZone={hideSecondBottomZone}
        showUpdatedFooterUI={showUpdatedFooterUI}
      />
    </>
  );

  return (
    <footer className={classNames("light" === theme ? "light-theme" : "dark-theme", styles.footerWrapper)} id="footer">
      {enableTopBanner && renderTopBanner && <div>{renderTopBanner()}</div>}
      <div className={styles.footerContainer}>
        {isExtended && (
          <div className={styles.footerExtendedContent}>
            <Container
              className={classNames(styles.wrapper, {
                [styles.wrapperWithListComponent]: withListComponent,
              })}
            >
              {children}
            </Container>
            {enableFooterTopZone && renderFooterTopZone && (
              <Container className={styles.footerTopEditableZone} dataTestId="footerTopZone">
                {renderFooterTopZone()}
              </Container>
            )}
          </div>
        )}
        <FooterHealthBanner {...healthBannerProps} />
        <Container className={styles.footerContentWrapper}>
          <FooterBreadcrumbs logoProps={logoProps} list={breadcrumbsList} renderLink={renderLink} />
          <FooterNav
            {...footerNavProps}
            showUpdatedFooterUI={showUpdatedFooterUI}
            openAssetsInNewTab={openAssetsInNewTab}
            withdrawalModalTriggerUrl={withdrawalModalTriggerUrl}
            onOpenWithdrawalModal={onOpenWithdrawalModal}
          />
          <div className={classNames({ [styles.footerBottomZoneContainer]: showUpdatedFooterUI })}>
            <FooterIcons
              socialTitle={socialTitle}
              socialIcons={socialIcons}
              paymentTitle={paymentTitle}
              paymentIcons={paymentIcons}
              renderSocialIcon={renderSocialIcon}
              showUpdatedFooterUI={showUpdatedFooterUI}
              flipIconsOrder={flipIconsOrder}
            />
            {enableFooterBottomZone &&
              (showUpdatedFooterUI ? (
                BottomZone
              ) : (
                <div className={classNames(styles.footerBottomZone)}>{BottomZone}</div>
              ))}
          </div>
          <Copyright
            {...copyrightProps}
            cookieSettingsAriaLabel={ariaLabels?.footerCookiesSettingsLink}
            withdrawalModalTriggerUrl={withdrawalModalTriggerUrl}
            onOpenWithdrawalModal={onOpenWithdrawalModal}
          />
        </Container>
      </div>
    </footer>
  );
};
